import { createFileRoute } from "@tanstack/react-router";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { ClipboardList, Gift, HandCoins, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const APPLY_URL = "https://coyotecreekhatco.bixgrow.com/register";
const LOGIN_URL = "https://coyotecreekhatco.bixgrow.com/login";

export const Route = createFileRoute("/creator-creek")({
  head: () => ({
    meta: [
      { title: "Creator Creek | Coyote Creek Hat Co." },
      {
        name: "description",
        content:
          "Join Creator Creek — our affiliate program for creators, outdoorsmen, and storytellers. Earn commission repping Coyote Creek in the field.",
      },
      { property: "og:title", content: "Creator Creek | Coyote Creek Hat Co." },
      {
        property: "og:description",
        content: "Join our affiliate program. Create, promote, and earn.",
      },
      { property: "og:url", content: "https://coyotecreekhatco.com/creator-creek" },
      { property: "og:image", content: "https://cdn.shopify.com/s/files/1/0808/1332/6620/files/205E6438-5994-4B06-A365-A9F2830E5363.png?v=1767042318" },
      { name: "twitter:image", content: "https://cdn.shopify.com/s/files/1/0808/1332/6620/files/205E6438-5994-4B06-A365-A9F2830E5363.png?v=1767042318" },
    ],
    links: [{ rel: "canonical", href: "https://coyotecreekhatco.com/creator-creek" }],
  }),
  component: CreatorCreekPage,
});

const STATS = [
  { value: "Up to 15%", label: "Commission" },
  { value: "30 Days", label: "Cookie Window" },
  { value: "24–48 hrs", label: "Approval Time" },
];

const STEPS = [
  {
    icon: ClipboardList,
    title: "Apply",
    desc: "Fill out your application. Give us 24–48 hours to review and we'll get back to you!",
  },
  {
    icon: Gift,
    title: "Gear'd Up",
    desc: "After being approved, we'll send you a grande discount to suit up along with your own custom link.",
  },
  {
    icon: HandCoins,
    title: "Create, Promote, & Earn",
    desc: "Share your affiliate link or coupon on your social media while creating and repping our brand, then start earning!",
  },
];

const BENEFITS = [
  "Join our heritage, rep our brand",
  "30-day cookie duration",
  "Competitive & transparent incentives",
  "Exclusive joining discounts & a personal discount code to share with followers",
];

const BUILT_FOR = ["Bloggers", "Influencers", "Content Creators", "Marketing Agencies", "& More"];

const FAQS = [
  {
    q: "How long does the approval process take until I become an affiliate?",
    a: "Fill out the sign-up form in a few minutes and we will review your application within 24–48 hours.",
  },
  {
    q: "How will I know if my application is approved?",
    a: "Our team will carefully review your application and if yours is approved, you'll receive an email confirming your acceptance along with promotion assets to help you get started. For denials, we'll also email you that your request has been rejected.",
  },
  {
    q: "How are referrals tracked?",
    a: "Upon acceptance into the affiliate program, you'll receive access to your own affiliate dashboard where you can manage your affiliate links and coupons. When customers purchase using your link or coupon, the sales are credited to you and earn you commissions. Even if customers don't order on the first visit, your affiliate cookie stays in their browser — you still earn commissions when they later purchase, as long as the cookie hasn't expired.",
  },
  {
    q: "How do I get paid with your affiliate program?",
    a: "Our affiliates get paid directly to the payment account of their choice, or with discounts converted from Store Credit.",
  },
  {
    q: "When do I get paid with your affiliate program?",
    a: "When you successfully bring in orders, we'll review these orders and pay commissions on the basis both parties agreed on — after each successful referral, when your balance reaches a certain amount, or on a weekly, bi-weekly, or monthly cadence.",
  },
  {
    q: "Who is this affiliate program for?",
    a: "Everyone and anyone with an interest in our business: bloggers, influencers, content creators, marketing agencies, and more.",
  },
];

function CreatorCreekPage() {
  const { data: settings, isLoading } = useSiteSettings();
  if (isLoading) return null;
  const isLive = !settings?.creator_creek_live;

  if (isLive) {
    return (
      <div>
        {/* Hero */}
        <section className="relative w-full h-[60vh] min-h-[420px] overflow-hidden">
          <img
            src="https://cdn.shopify.com/s/files/1/0808/1332/6620/files/205E6438-5994-4B06-A365-A9F2830E5363.png?v=1767042318"
            alt="Creator Creek"
            className="absolute inset-0 h-full w-full object-cover"
            width={1920}
            height={1080}
          />
          <div className="absolute inset-0 bg-black/50" />
          <div className="relative z-10 flex h-full flex-col items-center justify-center px-6 text-center">
            <h1 className="font-heading text-5xl md:text-7xl uppercase tracking-[0.15em] text-white drop-shadow-lg">
              Creator Creek
            </h1>
          </div>
        </section>

        {/* Coming soon message */}
        <div className="min-h-[40vh] flex items-center justify-center px-6 py-24 text-center">
          <div className="max-w-xl">
            <p className="text-sm uppercase tracking-[0.3em] text-[#C6A971] mb-4 font-ui">
              Creator Creek
            </p>
            <h2 className="text-4xl md:text-5xl mb-6 text-[#2A1510] font-heading">
              Affiliate Program Coming Soon
            </h2>
            <p className="text-[#2A1510]/80 text-lg font-body">
              We're saddling up something special for creators. Check back soon.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Hero */}
      <section className="relative w-full h-[60vh] min-h-[420px] overflow-hidden">
        <img
          src="https://cdn.shopify.com/s/files/1/0808/1332/6620/files/205E6438-5994-4B06-A365-A9F2830E5363.png?v=1767042318"
          alt="Creator Creek"
          className="absolute inset-0 h-full w-full object-cover"
          width={1920}
          height={1080}
        />
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 flex h-full flex-col items-center justify-center px-6 text-center">
          <h1 className="font-heading text-5xl md:text-7xl uppercase tracking-[0.15em] text-white drop-shadow-lg">
            Create &amp; Get Paid!
          </h1>
          <p className="mt-6 max-w-2xl font-body text-lg md:text-xl text-white/90 drop-shadow">
            Join our creators program and earn up to 15% commission on each sale. We'll take care of you.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <a
              href={APPLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-cc-accent tracking-widest"
            >
              Apply Now
            </a>
            <a
              href={LOGIN_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-cc-outline tracking-widest"
            >
              Login
            </a>
          </div>
        </div>
      </section>

      {/* Stat strip */}
      <section className="border-b border-border bg-card">
        <div className="mx-auto max-w-6xl px-6 py-10">
          <div className="grid gap-8 md:grid-cols-3">
            {STATS.map((s) => (
              <div key={s.label} className="text-center">
                <div className="font-heading text-4xl md:text-5xl text-blaze">{s.value}</div>
                <div className="mt-2 font-ui text-sm uppercase tracking-widest text-muted-foreground">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Plain & Simple — 3 steps */}
      <section className="py-20">
        <div className="mx-auto max-w-6xl px-6">
          <div className="text-center">
            <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-[0.15em] text-primary">
              Plain &amp; Simple
            </h2>
            <p className="mt-4 font-ui text-sm uppercase tracking-widest text-muted-foreground">
              To join our program, follow these 3 steps
            </p>
          </div>

          <div className="mt-14 grid gap-8 md:grid-cols-3">
            {STEPS.map((step, i) => {
              const Icon = step.icon;
              return (
                <div
                  key={step.title}
                  className="relative rounded-2xl border border-border bg-card p-8 text-center"
                >
                  <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-blaze/10 text-blaze">
                    <Icon className="!h-8 !w-8" aria-hidden="true" />
                  </div>
                  <div className="mt-4 font-ui text-xs uppercase tracking-widest text-muted-foreground">
                    Step {i + 1}
                  </div>
                  <h3 className="mt-2 font-heading text-2xl uppercase tracking-wider text-primary">
                    {step.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{step.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="bg-card py-20 border-y border-border">
        <div className="mx-auto max-w-5xl px-6">
          <div className="text-center">
            <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-[0.15em] text-primary">
              The Benefits of Joining Creator Creek
            </h2>
          </div>
          <div className="mt-12 grid gap-4 md:grid-cols-2">
            {BENEFITS.map((b) => (
              <div
                key={b}
                className="flex items-start gap-3 rounded-xl border border-border bg-background p-5"
              >
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blaze text-[#F5EFE2]">
                  <Check className="!h-4 !w-4" aria-hidden="true" />
                </span>
                <span className="font-ui text-base text-primary">{b}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Built For */}
      <section className="py-16">
        <div className="mx-auto max-w-5xl px-6 text-center">
          <h2 className="font-heading text-3xl md:text-4xl uppercase tracking-[0.15em] text-primary">
            Built For
          </h2>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            {BUILT_FOR.map((b) => (
              <span
                key={b}
                className="rounded-full border border-blaze/30 bg-blaze/10 px-5 py-2 font-ui text-sm uppercase tracking-widest text-blaze"
              >
                {b}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="bg-card py-20 border-y border-border">
        <div className="mx-auto max-w-3xl px-6">
          <div className="text-center">
            <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-[0.15em] text-primary">
              FAQS
            </h2>
          </div>
          <Accordion type="single" collapsible className="mt-10">
            {FAQS.map((item, i) => (
              <AccordionItem key={item.q} value={`faq-${i}`}>
                <AccordionTrigger className="font-ui text-left text-base text-primary">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20">
        <div className="mx-auto max-w-5xl px-6">
          <div className="rounded-2xl bg-blaze p-12 text-center">
            <h2 className="font-heading text-3xl md:text-5xl uppercase tracking-[0.15em] text-[#F5EFE2]">
              Join Our Team
            </h2>
            <p className="mt-4 font-body text-lg text-[#F5EFE2]/90">
              Become an affiliate and start earning today.
            </p>
            <div className="mt-8 flex justify-center">
              <a
                href={APPLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-cc-outline tracking-widest"
              >
                Apply Now
              </a>
            </div>
            <p className="mt-6 font-ui text-sm uppercase tracking-widest text-[#F5EFE2]/80">
              Questions? creators@coyotecreekhatco.com
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
